import React, { useState, useEffect, useMemo } from "react";
import TopNav from "../Navbars/TopNav";
import Sidebar from "../Navbars/sidebar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  useGetInternByid,
  useResetAttendanceDevice,
  UseUserinfo,
  useSubmitAssessment,
} from "../../adapters/Requests";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Checkbox } from "@mui/material";
import { toast } from "sonner";
import {
  CheckCircle,
  ClockCounterClockwise,
  FileText,
  Warning,
  HourglassSimple,
  Info,
  CalendarBlank,
  Briefcase,
  House,
  ArrowLeft,
  CaretRight,
  Clock,
  CalendarCheck,
  SignIn,
  SignOut,
  Bank,
} from "@phosphor-icons/react";

function MonthlyAssessmentForm() {
  const { id: internId } = useParams();
  const navigate = useNavigate();
  const { data: internData, refetch } = useGetInternByid(internId);
  const { data: userData } = UseUserinfo();
  const { mutate: resetDevice } = useResetAttendanceDevice();
  const { mutateAsync: submitAssessment } = useSubmitAssessment();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [activeMonthYear, setActiveMonthYear] = useState(null);

  const [startDay] = useState(
    parseInt(import.meta.env.VITE_ASSESSMENT_START_DAY) || 20,
  );

  const [bankDetails, setBankDetails] = useState({
    bankName: "",
    accountNumber: "",
    sortCode: "",
  });

  const [formValues, setFormValues] = useState({
    PUNCTUALITY: "",
    DEDICATION_TO_DUTY: "",
    SAFETY_CONDUCT: "",
    GENERAL_CONDUCT: "",
    ABILITY_TO_LEARN: "",
    SUPERVISOR_COMMENT: "",
    BANK_UPDATE_REQUESTED: false,
    BANK_UPDATE_NOTE: "",
  });

  const currentCalendarMonth = useMemo(() => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const now = new Date();
    return `${months[now.getMonth()]} ${now.getFullYear()}`;
  }, []);

  useEffect(() => {
    if (!activeMonthYear) {
      setActiveMonthYear(currentCalendarMonth);
    }
  }, [currentCalendarMonth, activeMonthYear]);

  const activeMonthData = useMemo(() => {
    if (!activeMonthYear) return { monthIndex: -1, year: -1 };
    const [monthName, yearStr] = activeMonthYear.split(" ");
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return {
      monthIndex: months.indexOf(monthName),
      year: parseInt(yearStr),
    };
  }, [activeMonthYear]);

  const monthlyAttendanceStats = useMemo(() => {
    if (!internData?.MonthlyAttendances || activeMonthData.monthIndex === -1)
      return null;
    return internData.MonthlyAttendances.find(
      (m) =>
        m.month === activeMonthData.monthIndex + 1 &&
        m.year === activeMonthData.year,
    );
  }, [internData, activeMonthData]);

  const dailyAttendanceLogs = useMemo(() => {
    if (!internData?.DailyAttendances || activeMonthData.monthIndex === -1)
      return [];
    return internData.DailyAttendances.filter((record) => {
      const d = new Date(record.date);
      return (
        d.getMonth() === activeMonthData.monthIndex &&
        d.getFullYear() === activeMonthData.year
      );
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [internData, activeMonthData]);

  const allAssessments = useMemo(() => {
    if (!internData?.InternMonthlyAssessments) return [];

    return [...internData.InternMonthlyAssessments].sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
    );
  }, [internData]);

  const viewAssessment = useMemo(() => {
    let found = allAssessments.find(
      (a) => a.month_year_of_assessment === activeMonthYear,
    );

    if (!found && activeMonthYear === currentCalendarMonth) {
      return {
        month_year_of_assessment: currentCalendarMonth,
        InternMonthlyActivitys: [],
        isPlaceholder: true,
      };
    }

    return found || null;
  }, [allAssessments, activeMonthYear, currentCalendarMonth]);

  const viewActivities = useMemo(() => {
    if (viewAssessment && !viewAssessment.isPlaceholder) {
      return viewAssessment.InternMonthlyActivitys || [];
    }
    return viewAssessment?.InternMonthlyActivitys || [];
  }, [viewAssessment]);

  const isCurrentMonth = activeMonthYear === currentCalendarMonth;
  const programType = internData?.application?.vacancy?.vacancy_position?.name;
  const isExemptFromActivities = ["GAP", "IT"].includes(programType);

  const isViewCompleted = useMemo(() => {
    if (!viewAssessment) return false;
    if (viewAssessment.isPlaceholder) return false;

    return !!(
      viewAssessment.punctuality_score ||
      viewAssessment.dedication_to_duty_score
    );
  }, [viewAssessment]);

  const isWindowOpen = useMemo(() => {
    const today = new Date().getDate();
    return today >= startDay;
  }, [startDay]);

  useEffect(() => {
    if (viewAssessment && !viewAssessment.isPlaceholder) {
      let rawComment = viewAssessment.supervisors_comment || "";
      let cleanComment = rawComment;
      let isBankRequest = false;
      let extractedBank = { bankName: "", accountNumber: "", sortCode: "" };

      const bankTag = "[URGENT: BANK DETAILS UPDATE REQUESTED]";

      if (rawComment.includes(bankTag)) {
        isBankRequest = true;

        const parts = rawComment.split(bankTag);
        cleanComment = parts[0].trim();

        let detailsStr = parts[1];

        if (detailsStr) {
          if (detailsStr.includes("---")) {
            detailsStr = detailsStr.split("---")[0];
          }

          detailsStr = detailsStr.trim();

          const pairs = detailsStr.split("||");
          pairs.forEach((p) => {
            const [k, v] = p.split(":");
            if (k && v) {
              const key = k.trim();
              const val = v.trim();
              if (key === "BANK_NAME") extractedBank.bankName = val;
              if (key === "ACC_NO") extractedBank.accountNumber = val;
              if (key === "SORT") extractedBank.sortCode = val;
            }
          });
        }
      } else {
        cleanComment = cleanComment.split("---")[0].trim();
      }

      setFormValues({
        PUNCTUALITY: viewAssessment.punctuality_score || "",
        DEDICATION_TO_DUTY: viewAssessment.dedication_to_duty_score || "",
        SAFETY_CONDUCT: viewAssessment.safety_conduct_score || "",
        GENERAL_CONDUCT: viewAssessment.general_conduct_score || "",
        ABILITY_TO_LEARN: viewAssessment.ability_to_learn_score || "",
        SUPERVISOR_COMMENT: cleanComment,
        BANK_UPDATE_REQUESTED: isBankRequest,
        BANK_UPDATE_NOTE: "",
      });

      if (isBankRequest) {
        setBankDetails(extractedBank);
      } else {
        setBankDetails({ bankName: "", accountNumber: "", sortCode: "" });
      }
    } else {
      setFormValues({
        PUNCTUALITY: "",
        DEDICATION_TO_DUTY: "",
        SAFETY_CONDUCT: "",
        GENERAL_CONDUCT: "",
        ABILITY_TO_LEARN: "",
        SUPERVISOR_COMMENT: "",
        BANK_UPDATE_REQUESTED: false,
        BANK_UPDATE_NOTE: "",
      });
      setBankDetails({ bankName: "", accountNumber: "", sortCode: "" });
    }
  }, [viewAssessment]);

  const isReadOnly = useMemo(() => {
    if (!isCurrentMonth) return true;
    if (isViewCompleted) return true;
    if (!isWindowOpen) return true;

    if (viewActivities.length === 0 && !isExemptFromActivities) return true;
    return false;
  }, [
    isCurrentMonth,
    isViewCompleted,
    isWindowOpen,
    viewActivities,
    isExemptFromActivities,
  ]);

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleResetDevice = () => {
    resetDevice(internId);
    setIsResetDialogOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!viewAssessment) return;

    setIsSubmitting(true);
    let isSuccess = false;
    let bankRequestString = null;
    if (formValues.BANK_UPDATE_REQUESTED) {
      bankRequestString = `BANK_NAME:${bankDetails.bankName}||ACC_NO:${bankDetails.accountNumber}||SORT:${bankDetails.sortCode}`;
    }

    try {
      await submitAssessment({
        assessment_id: viewAssessment.id,
        scores: {
          punctuality: formValues.PUNCTUALITY,
          dedication: formValues.DEDICATION_TO_DUTY,
          safety: formValues.SAFETY_CONDUCT,
          general_conduct: formValues.GENERAL_CONDUCT,
          ability: formValues.ABILITY_TO_LEARN,
        },
        comments: formValues.SUPERVISOR_COMMENT,
        bank_update_request: bankRequestString,
        supervisor_details: {
          name: `${userData.first_name} ${userData.last_name}`,
          id: userData.staff_id,
          designation: userData.business_unit?.name || "Supervisor",
        },
      });

      await refetch();
      isSuccess = true;
    } catch (error) {
      toast.error(error.response?.data?.message || "Submission Failed");
    } finally {
      setIsSubmitting(false);
    }

    if (isSuccess) {
      toast.success("Assessment endorsed successfully.");
    }
  };

  const historyList = useMemo(() => {
    const list = allAssessments.map((a) => ({
      month: a.month_year_of_assessment,
      isCompleted: !!a.punctuality_score,
      id: a.id,
    }));

    const hasCurrent = list.some((i) => i.month === currentCalendarMonth);
    if (!hasCurrent) {
      list.unshift({
        month: currentCalendarMonth,
        isCompleted: false,
        id: "current-placeholder",
      });
    }

    return list;
  }, [allAssessments, currentCalendarMonth]);

  const formatTime = (isoString) => {
    if (!isoString) return "--:--";
    return new Date(isoString).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  return (
    <>
      <div className="flex h-screen overflow-hidden bg-slate-50">
        <div className="relative flex-1 flex flex-col overflow-y-auto overflow-x-hidden">
          <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
            <div className="flex items-center justify-between w-full mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Link
                  to="/dashboard"
                  className="flex items-center hover:text-green-600 transition-colors"
                >
                  <House size={16} />
                </Link>
                <CaretRight size={16} className="text-gray-300" />
                <span className="font-semibold text-gray-900">
                  Assessment Form
                </span>
              </div>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft size={16} /> Back
              </button>
            </div>

            <div className="relative flex items-center justify-center mb-6 min-h-[50px]">
              <div className="text-center">
                <h1 className="text-xl text-slate-800 font-medium">
                  <span className="font-bold">
                    {internData?.first_name} {internData?.last_name}
                  </span>{" "}
                  <span className="font-bold text-slate-700">
                    ({activeMonthYear})
                  </span>{" "}
                  {isCurrentMonth && (
                    <span className="bg-green-100 text-green-700 text-[10px] px-2 rounded-full font-bold ml-2 align-middle">
                      CURRENT
                    </span>
                  )}
                </h1>
              </div>
              <button
                type="button"
                onClick={() => setIsResetDialogOpen(true)}
                className="absolute right-0 px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg shadow-sm hover:bg-red-50 focus:outline-none transition-colors"
              >
                Reset Device
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div
                  onClick={() => setAttendanceModalOpen(true)}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center justify-between cursor-pointer hover:border-green-400 hover:shadow-md transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-green-50 text-green-600 rounded-lg group-hover:bg-green-100 transition-colors">
                      <Clock size={24} weight="fill" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-800">
                        Attendance Overview
                      </h3>
                      <p className="text-xs text-slate-500">
                        View detailed logs for {activeMonthYear}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {monthlyAttendanceStats ? (
                      <div className="text-right">
                        <span className="block text-lg font-bold text-slate-800 leading-none">
                          {monthlyAttendanceStats.days_present}
                        </span>
                        <span className="text-[10px] text-slate-500 uppercase font-medium">
                          Days Present
                        </span>
                      </div>
                    ) : (
                      <div className="text-right">
                        <span className="block text-lg font-bold text-slate-800 leading-none">
                          {dailyAttendanceLogs.length}
                        </span>
                        <span className="text-[10px] text-slate-500 uppercase font-medium">
                          Days Logged
                        </span>
                      </div>
                    )}
                    <CaretRight size={16} className="text-slate-300" />
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4 border-b pb-2">
                    <div className="flex items-center gap-2">
                      <FileText size={20} className="text-green-600" />
                      <h2 className="text-sm font-bold text-slate-900 uppercase">
                        Intern's Logged Activities
                      </h2>
                    </div>
                    {viewActivities.length > 0 && (
                      <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full border border-green-200">
                        {viewActivities.length} Entries
                      </span>
                    )}
                  </div>

                  {viewActivities.length === 0 ? (
                    isExemptFromActivities ? (
                      <div className="p-6 bg-green-50 text-green-800 rounded-lg flex items-start gap-4 border border-green-100">
                        <Info
                          size={28}
                          className="flex-shrink-0 mt-1 text-green-500"
                        />
                        <div>
                          <p className="font-bold text-sm mb-1">
                            Activities Not Required
                          </p>
                          <p className="text-xs leading-relaxed opacity-90">
                            IT/GAP interns are not required to log monthly
                            activities. You may proceed directly with the
                            assessment.
                          </p>
                        </div>
                      </div>
                    ) : isCurrentMonth ? (
                      <div className="p-6 bg-gray-50 text-gray-800 rounded-lg flex items-start gap-4 border border-gray-100">
                        <ClockCounterClockwise
                          size={28}
                          className="flex-shrink-0 mt-1"
                        />
                        <div>
                          <p className="font-bold text-sm mb-1">
                            Waiting for Submission
                          </p>
                          <p className="text-xs leading-relaxed opacity-90">
                            Intern has not submitted activities for{" "}
                            {activeMonthYear} yet.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="p-8 text-center bg-slate-50 border border-dashed border-slate-200 rounded-lg">
                        <p className="text-sm text-slate-500">
                          No activities were logged for this month.
                        </p>
                      </div>
                    )
                  ) : (
                    <div className="grid gap-4">
                      {viewActivities.map((act, i) => (
                        <div
                          key={act.id || i}
                          className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden hover:border-green-300 transition-colors"
                        >
                          <div className="bg-slate-100/50 px-4 py-3 border-b border-slate-200 flex flex-wrap justify-between items-center gap-2">
                            <div className="flex items-center gap-2">
                              <Briefcase size={16} className="text-slate-500" />
                              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                                {act.activity_name || `Activity ${i + 1}`}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 bg-white px-2 py-1 rounded border border-slate-200 shadow-sm">
                              <span className="text-[11px] font-medium text-slate-600 font-mono">
                                {act.activity_duration || "N/A"}
                              </span>
                            </div>
                          </div>
                          <div className="p-4">
                            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                              {act.description || "No description provided."}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 relative"
                >
                  {isReadOnly && (
                    <div className="absolute inset-0 bg-slate-50/30 z-10 cursor-not-allowed" />
                  )}

                  <div className="flex justify-between items-center mb-6 border-b pb-2 relative z-20">
                    <h2 className="text-sm font-bold text-slate-900 uppercase">
                      Supervisor Evaluation ({activeMonthYear})
                    </h2>
                    {isViewCompleted ? (
                      <span className="bg-green-100 text-green-800 text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1.5 border border-green-200">
                        <CheckCircle weight="fill" /> Completed
                      </span>
                    ) : isReadOnly && isCurrentMonth ? (
                      <span className="bg-yellow-100 text-gray-800 text-xs font-bold px-2.5 py-1 rounded-full border border-gray-200">
                        Inaccessible
                      </span>
                    ) : (
                      <span className="bg-green-100 text-green-800 text-xs font-bold px-2.5 py-1 rounded-full border border-green-200">
                        Pending Action
                      </span>
                    )}
                  </div>
                  {isReadOnly && (
                    <div className="mb-6 relative z-20">
                      {isViewCompleted ? (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                          This assessment has been submitted and finalized.
                        </div>
                      ) : !isCurrentMonth ? (
                        <div className="p-3 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-600">
                          This is a past record. Editing is disabled.
                        </div>
                      ) : !isWindowOpen ? (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-sm text-green-800">
                          <Info size={18} />
                          Window opens on the <strong>{startDay}th</strong>.
                        </div>
                      ) : (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800">
                          Cannot assess until intern submits activities.
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6 relative z-20">
                    {[
                      "PUNCTUALITY",
                      "DEDICATION_TO_DUTY",
                      "SAFETY_CONDUCT",
                      "GENERAL_CONDUCT",
                      "ABILITY_TO_LEARN",
                    ].map((field) => (
                      <div key={field}>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">
                          {field.replace(/_/g, " ")}
                        </label>
                        <select
                          name={field}
                          value={formValues[field] || ""}
                          onChange={handleInputChange}
                          disabled={isReadOnly}
                          className="w-full rounded-lg border-slate-300 text-sm py-2.5 disabled:bg-white disabled:text-slate-600 focus:ring-green-500 focus:border-green-500"
                          required
                        >
                          <option value="">Select Rating...</option>
                          {[1, 2, 3, 4, 5].map((n) => (
                            <option key={n} value={n}>
                              {n} -{" "}
                              {
                                [
                                  "Unsatisfactory",
                                  "Fair",
                                  "Good",
                                  "Very Good",
                                  "Excellent",
                                ][n - 1]
                              }
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  <div className="mb-6 relative z-20">
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">
                      Remarks
                    </label>
                    <textarea
                      name="SUPERVISOR_COMMENT"
                      value={formValues.SUPERVISOR_COMMENT}
                      onChange={handleInputChange}
                      disabled={isReadOnly}
                      rows={4}
                      className="w-full rounded-lg border-slate-300 text-sm disabled:bg-white disabled:text-slate-600 focus:ring-green-500 focus:border-green-500"
                      placeholder={
                        isReadOnly ? "No remarks." : "Enter feedback..."
                      }
                      required
                    />
                  </div>

                  {(formValues.BANK_UPDATE_REQUESTED || !isReadOnly) && (
                    <div className="bg-orange-50 border border-orange-100 rounded-lg p-4 mb-6 relative z-20">
                      <div className="flex items-center gap-2 mb-3">
                        <Checkbox
                          checked={formValues.BANK_UPDATE_REQUESTED}
                          onChange={handleInputChange}
                          name="BANK_UPDATE_REQUESTED"
                          color="warning"
                          size="small"
                          sx={{ padding: 0 }}
                          disabled={isReadOnly}
                        />
                        <div className="flex items-center gap-2 ml-2">
                          <label className="text-sm font-bold text-orange-900 cursor-pointer">
                            Request Bank Details Update?
                          </label>
                          {formValues.BANK_UPDATE_REQUESTED && isReadOnly && (
                            <span className="text-[10px] bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full font-bold">
                              REQUESTED
                            </span>
                          )}
                        </div>
                      </div>

                      {formValues.BANK_UPDATE_REQUESTED && (
                        <div className="mt-3 pl-7 grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="text-[10px] uppercase font-bold text-orange-700">
                              New Bank Name
                            </label>
                            <input
                              type="text"
                              name="bankName"
                              value={bankDetails.bankName}
                              onChange={handleBankChange}
                              className="w-full text-sm border-orange-200 rounded-md focus:ring-orange-500 py-2 px-3 disabled:bg-orange-100/50 disabled:text-orange-900"
                              required={formValues.BANK_UPDATE_REQUESTED}
                              disabled={isReadOnly}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase font-bold text-orange-700">
                              Account Number
                            </label>
                            <input
                              type="text"
                              name="accountNumber"
                              value={bankDetails.accountNumber}
                              onChange={handleBankChange}
                              maxLength={10}
                              className="w-full text-sm border-orange-200 rounded-md focus:ring-orange-500 py-2 px-3 disabled:bg-orange-100/50 disabled:text-orange-900"
                              required={formValues.BANK_UPDATE_REQUESTED}
                              disabled={isReadOnly}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase font-bold text-orange-700">
                              Sort Code
                            </label>
                            <input
                              type="text"
                              name="sortCode"
                              value={bankDetails.sortCode}
                              onChange={handleBankChange}
                              className="w-full text-sm border-orange-200 rounded-md focus:ring-orange-500 py-2 px-3 disabled:bg-orange-100/50 disabled:text-orange-900"
                              disabled={isReadOnly}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!isReadOnly && (
                    <div className="flex justify-end pt-4 border-t border-slate-100 relative z-20">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`bg-green-600 hover:bg-green-700 text-white px-8 py-2.5 rounded-lg text-sm font-bold flex items-center gap-2 transition-all shadow-sm ${
                          isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                        }`}
                      >
                        {isSubmitting ? (
                          "Submitting..."
                        ) : (
                          <>
                            <CheckCircle size={18} weight="bold" /> Submit
                            Endorsement
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </form>
              </div>
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 sticky top-24">
                  <div className="flex items-center gap-2 mb-4 border-b pb-3">
                    <HourglassSimple size={18} className="text-slate-400" />
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                      Assessment Timeline
                    </h3>
                  </div>

                  <div className="space-y-2">
                    {historyList.map((item) => {
                      const isActive = item.month === activeMonthYear;
                      const isCurrent = item.month === currentCalendarMonth;

                      return (
                        <div
                          key={item.month}
                          onClick={() => setActiveMonthYear(item.month)}
                          className={`
                            p-3 rounded-lg border cursor-pointer transition-all flex justify-between items-center group
                            ${
                              isActive
                                ? "bg-green-50 border-green-500 shadow-sm"
                                : "bg-white border-slate-100 hover:bg-slate-50 hover:border-slate-300"
                            }
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-2 h-2 rounded-full ${
                                isActive ? "bg-green-600" : "bg-slate-300"
                              }`}
                            ></div>
                            <div>
                              <span
                                className={`block text-sm font-bold ${
                                  isActive ? "text-green-800" : "text-slate-700"
                                }`}
                              >
                                {item.month}
                              </span>
                              {isCurrent && (
                                <span className="text-[10px] text-green-600 font-medium">
                                  Current Month
                                </span>
                              )}
                            </div>
                          </div>

                          {item.isCompleted ? (
                            <CheckCircle
                              size={18}
                              weight="fill"
                              className="text-green-500"
                            />
                          ) : (
                            <div className="w-4 h-4 rounded-full border-2 border-slate-200 group-hover:border-slate-400"></div>
                          )}
                        </div>
                      );
                    })}

                    {historyList.length === 0 && (
                      <p className="text-sm text-slate-400 text-center py-4">
                        No timeline data available.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      <Dialog open={attendanceModalOpen} onOpenChange={setAttendanceModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-800">
              <CalendarCheck size={24} className="text-green-600" />
              Attendance Log - {activeMonthYear}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {dailyAttendanceLogs.length === 0 ? (
              <div className="text-center py-10 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                <Clock size={32} className="mx-auto text-slate-300 mb-2" />
                <p className="text-slate-500 text-sm">
                  No attendance records found for this month.
                </p>
              </div>
            ) : (
              <div className="overflow-hidden border border-slate-200 rounded-lg">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium">Arrival</th>
                      <th className="px-4 py-3 font-medium">Departure</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {dailyAttendanceLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-700">
                          {formatDate(log.date)}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          <div className="flex items-center gap-1.5">
                            <SignIn size={14} className="text-green-500" />
                            {formatTime(log.clocked_in_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          <div className="flex items-center gap-1.5">
                            <SignOut size={14} className="text-red-500" />
                            {formatTime(log.clocked_out_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                              log.status === "PRESENT"
                                ? "bg-green-50 text-green-700 border-green-200"
                                : "bg-red-50 text-red-700 border-red-200"
                            }`}
                          >
                            {log.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Warning size={20} weight="fill" />
              Confirm Device Reset
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              This action will reset the intern's registered device
              authentication.
              <br />
              <strong>Are you sure you want to proceed?</strong>
            </p>
          </div>
          <DialogFooter>
            <button
              onClick={() => setIsResetDialogOpen(false)}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleResetDevice}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors ml-2"
            >
              Reset Device
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default MonthlyAssessmentForm;
